import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  MODAL_TYPE,
  type CommonModalOptions,
  type ModalType
} from '@/features/miiaFw/types/components/commonModal'
import MiiaFwConfig from '@/configs/config'

export const useCommonModalStore = defineStore('commonModalStore', () => {
  const options = ref<CommonModalOptions>({
    size: 'sm',
    title: MiiaFwConfig.config.sysName,
    body: '',
    okTitle: 'OK',
    cancelTitle: 'キャンセル',
    okOnly: false,
    autofocusButton: undefined,
    okColor: 'primary'
  })
  const isOpen = ref(false)
  const storeResolve = ref<((value: string | PromiseLike<string>) => void) | null>(null)
  const storeReject = ref<((value: string | PromiseLike<string>) => void) | null>(null)

  const size = computed(() => {
    return options.value.size
  })

  const title = computed(() => {
    return options.value.title
  })

  const body = computed(() => {
    return options.value.body
  })

  const okTitle = computed(() => {
    return options.value.okTitle
  })

  const cancelTitle = computed(() => {
    return options.value.cancelTitle
  })

  const okOnly = computed(() => {
    return options.value.okOnly
  })

  const autofocusButton = computed(() => {
    return options.value.autofocusButton
  })

  const okColor = computed(() => {
    return options.value.okColor
  })

  const openAlertModal = (_options: Omit<CommonModalOptions, 'okOnly'>) => {
    isOpen.value = true
    options.value = _getOptions(_options, MODAL_TYPE.alert)
    return new Promise<string>((resolve, reject) => {
      storeResolve.value = resolve
      storeReject.value = reject
    })
  }

  const openConfirmModal = (_options: Omit<CommonModalOptions, 'okOnly'>) => {
    isOpen.value = true
    options.value = _getOptions(_options, MODAL_TYPE.confirm)
    return new Promise<string>((resolve, reject) => {
      storeResolve.value = resolve
      storeReject.value = reject
    })
  }

  const openConfirmModalAsync = async (
    _options: Omit<CommonModalOptions, 'okOnly'>
  ): Promise<boolean> => {
    return await openConfirmModal(_options)
      .then(() => true)
      .catch(() => false)
  }

  const ok = () => {
    storeResolve.value?.('ok')
    _resetOptions()
  }
  const cancel = () => {
    storeReject.value?.('cancel')
    _resetOptions()
  }

  const _resetOptions = () => {
    options.value = {
      size: 'sm',
      title: MiiaFwConfig.config.sysName,
      body: '',
      okTitle: 'OK',
      cancelTitle: 'キャンセル',
      okOnly: false,
      autofocusButton: undefined,
      okColor: 'primary'
    }
  }

  const _getOptions = (
    options: Omit<CommonModalOptions, 'okOnly'>,
    type: ModalType
  ): CommonModalOptions => {
    const { title, body, okTitle, cancelTitle, autofocusButton, size, okColor } = options
    const _options = {
      size: size === undefined ? 'sm' : size,
      title: title === undefined ? MiiaFwConfig.config.sysName : title,
      body: body,
      okTitle: okTitle === undefined ? 'OK' : okTitle,
      cancelTitle: cancelTitle == undefined ? 'キャンセル' : cancelTitle,
      okOnly: type === MODAL_TYPE.alert ? true : false,
      autofocusButton: autofocusButton,
      okColor: okColor === undefined ? 'primary' : okColor
    }
    return _options
  }

  const reset = () => {
    options.value = {
      size: 'sm',
      title: MiiaFwConfig.config.sysName,
      body: '',
      okTitle: 'OK',
      cancelTitle: 'キャンセル',
      okOnly: false,
      autofocusButton: undefined,
      okColor: 'primary'
    }
    isOpen.value = false
    storeResolve.value = null
    storeReject.value = null
  }

  return {
    options,
    isOpen,
    size,
    title,
    body,
    okTitle,
    cancelTitle,
    okOnly,
    autofocusButton,
    okColor,
    openAlertModal,
    openConfirmModal,
    openConfirmModalAsync,
    ok,
    cancel,
    reset
  }
})
